import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Loader2, User, Building2, X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface ClientAddress {
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  country?: string;
}

export interface SoftecClient {
  id: string;
  name: string;
  clientType?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: ClientAddress;
}

interface CustomerComboboxProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (client: SoftecClient | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CustomerCombobox({
  value,
  onChange,
  onSelect,
  placeholder = "Kunde auswählen...",
  className = "",
  disabled = false
}: CustomerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SoftecClient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<SoftecClient | null>(null);
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

  // Search clients when query changes
  const searchClients = useCallback(async (query: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/softec-db/clients/search?q=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();

      if (data.clients && Array.isArray(data.clients)) {
        setResults(data.clients);
      } else {
        setResults([]);
      }
    } catch (error) {
      console.error('Client search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchClients(searchQuery);
    }, searchQuery.length === 0 ? 0 : 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, searchClients]);

  // Handle client selection
  const handleSelect = (client: SoftecClient) => {
    setSelectedClient(client);
    onChange(client.name);
    setOpen(false);
    setSearchQuery('');

    if (onSelect) {
      onSelect(client);
    }
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedClient(null);
    onChange('');
    setSearchQuery('');

    if (onSelect) {
      onSelect(null);
    }
  };

  // Handle manual input (typing without selecting)
  const handleManualInput = () => {
    if (searchQuery && !selectedClient) {
      onChange(searchQuery);
      setOpen(false);
    }
  };

  // Format address for display
  const formatAddress = (address?: ClientAddress): string => {
    if (!address) return '';
    const parts = [];
    if (address.street) {
      parts.push(`${address.street}${address.houseNumber ? ` ${address.houseNumber}` : ''}`);
    }
    if (address.zip || address.city) {
      parts.push(`${address.zip || ''} ${address.city || ''}`.trim());
    }
    return parts.join(', ');
  };

  const displayValue = selectedClient ? selectedClient.name : value;
  const hasValue = displayValue && displayValue.length > 0;

  return (
    <Popover open={open} onOpenChange={(o) => {
      setOpen(o);
      if (o && results.length === 0) searchClients(searchQuery);
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !hasValue && "text-muted-foreground",
            className
          )}
        >
          <div className="flex items-center gap-2 truncate flex-1 text-left">
            {selectedClient ? (
              <>
                <div className={cn(
                  "p-1 rounded flex-shrink-0",
                  selectedClient.clientType === 'business' ? 'bg-blue-100' : 'bg-green-100'
                )}>
                  {selectedClient.clientType === 'business' ? (
                    <Building2 className="h-3 w-3 text-blue-600" />
                  ) : (
                    <User className="h-3 w-3 text-green-600" />
                  )}
                </div>
                <span className="truncate">{selectedClient.name}</span>
              </>
            ) : hasValue ? (
              <span className="truncate">{displayValue}</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {hasValue && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && results.length === 0) {
                  handleManualInput();
                }
              }}
              placeholder="Kunde suchen..."
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
            {isLoading && <Loader2 className="h-4 w-4 animate-spin opacity-50" />}
          </div>
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                Suche...
              </div>
            ) : results.length === 0 ? (
              <div className="py-4 px-3">
                <p className="text-sm text-muted-foreground text-center mb-3">
                  Kein Kunde gefunden
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onChange(searchQuery);
                    setOpen(false);
                  }}
                >
                  "{searchQuery}" als manuellen Eintrag verwenden
                </Button>
              </div>
            ) : (
              <CommandGroup heading="Softec Kunden">
                {results.map((client) => (
                  <CommandItem
                    key={client.id}
                    value={client.id}
                    onSelect={() => handleSelect(client)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-3 w-full py-1">
                      <div className={cn(
                        "mt-0.5 p-1.5 rounded flex-shrink-0",
                        client.clientType === 'business' ? 'bg-blue-100' : 'bg-green-100'
                      )}>
                        {client.clientType === 'business' ? (
                          <Building2 className="h-4 w-4 text-blue-600" />
                        ) : (
                          <User className="h-4 w-4 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{client.name}</span>
                          {selectedClient?.id === client.id && (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                        {client.contactPerson && (
                          <p className="text-xs text-muted-foreground truncate">
                            {client.contactPerson}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-x-3 mt-1">
                          {client.email && (
                            <span className="text-xs text-muted-foreground">
                              {client.email}
                            </span>
                          )}
                          {client.phone && (
                            <span className="text-xs text-muted-foreground">
                              {client.phone}
                            </span>
                          )}
                        </div>
                        {client.address && formatAddress(client.address) && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {formatAddress(client.address)}
                          </p>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CustomerCombobox;
